import React, { useEffect, useState } from "react";
import axios from "axios";
import TabNavigation from "components/ui/TabNavigation";
import Icon from "components/AppIcon";

const ResultsAndDownloadCenter = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:5002/v1/api";
        const resp = await axios.get(`${baseUrl}/qcm/results`);
        setResults(resp?.data?.data?.results || []);
      } catch (e) {
        console.error(e);
        setError("Impossible de charger les résultats.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDownloadCsv = async (id, name, password) => {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:5002/v1/api";
      const resp = await axios.get(`${baseUrl}/qcm/${id}`, {
        params: { password },
      });
      const result = resp?.data?.data?.result;
      const answers = result?.answers || [];

      const header = [
        "QuestionNumber",
        "QuestionId",
        "QuestionText",
        "SelectedAnswer",
        "CorrectAnswer",
        "IsCorrect",
      ].join(";");

      const rows = answers.map((a, index) =>
        [
          index + 1,
          a.questionId,
          `"${(a.questionText || "").replace(/"/g, '""')}"`,
          `"${(a.selectedOptionText || "").replace(/"/g, '""')}"`,
          `"${(a.correctOptionText || "").replace(/"/g, '""')}"`,
          a.isCorrect ? "true" : "false",
        ].join(";")
      );

      const csv = [header, ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (name || "participant").replace(/\s+/g, "_").toLowerCase();
      link.download = `qcm_${safeName}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 403) {
        setPasswordError("Mot de passe incorrect.");
      } else {
        alert("Erreur lors de la génération du CSV.");
      }
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "var(--color-background)" }}
    >
      <TabNavigation />
      <div className="h-[60px]" />
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10 pb-20 md:pb-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1
              className="text-2xl md:text-3xl lg:text-4xl font-bold"
              style={{ color: "var(--color-text-primary)" }}
            >
              Résultats du QCM
            </h1>
            <p className="text-sm md:text-base mt-1" style={{ color: "var(--color-muted-foreground)" }}>
              Liste des participations et téléchargement des réponses
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm" style={{ color: "var(--color-text-primary)" }}>
            Chargement des résultats...
          </p>
        ) : error ? (
          <p className="text-sm" style={{ color: "var(--color-error)" }}>
            {error}
          </p>
        ) : results.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
            Aucun participant pour le moment.
          </p>
        ) : (
          <div
            className="rounded-2xl border"
            style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-card)" }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--color-border)" }}>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: "var(--color-muted-foreground)" }}>
                      Participant
                    </th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: "var(--color-muted-foreground)" }}>
                      Email
                    </th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: "var(--color-muted-foreground)" }}>
                      Score
                    </th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: "var(--color-muted-foreground)" }}>
                      Date
                    </th>
                    <th className="px-4 py-3 text-left font-semibold" style={{ color: "var(--color-muted-foreground)" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => {
                    const percentage =
                      r.totalQuestions > 0 ? Math.round((r.score / r.totalQuestions) * 100) : 0;
                    const passed = percentage >= 50;
                    const date = r.createdAt
                      ? new Date(r.createdAt).toLocaleString("fr-FR")
                      : "-";
                    return (
                      <tr
                        key={r.id}
                        className="border-b last:border-b-0"
                        style={{ borderColor: "var(--color-border)" }}
                      >
                        <td className="px-4 py-3" style={{ color: "var(--color-text-primary)" }}>
                          {r.name}
                        </td>
                        <td className="px-4 py-3" style={{ color: "var(--color-muted-foreground)" }}>
                          {r.email}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs">
                            {r.score}/{r.totalQuestions} ({percentage}%)
                          </span>
                          <span
                            className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{
                              backgroundColor: passed
                                ? "rgba(22,163,74,0.12)"
                                : "rgba(220,38,38,0.12)",
                              color: passed ? "var(--color-success)" : "var(--color-error)",
                            }}
                          >
                            {passed ? "Réussi" : "Échoué"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                          {date}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => {
                              setSelectedResult(r);
                              setPasswordValue("");
                              setPasswordError(null);
                              setPasswordModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium"
                            style={{
                              backgroundColor: "var(--color-primary)",
                              color: "var(--color-primary-foreground)",
                            }}
                          >
                            <Icon name="Download" size={14} />
                            CSV
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Modal mot de passe pour CSV */}
        {passwordModalOpen && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50 px-4"
            style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          >
            <div
              className="w-full max-w-sm rounded-xl p-6"
              style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}
            >
              <h2
                className="text-lg font-semibold mb-2"
                style={{ color: "var(--color-text-primary)" }}
              >
                Mot de passe requis
              </h2>
              <p
                className="text-xs mb-4"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                Entrez le mot de passe pour télécharger le CSV des réponses.
              </p>
              <input
                type="password"
                value={passwordValue}
                onChange={(e) => {
                  setPasswordValue(e.target.value);
                  setPasswordError(null);
                }}
                className="w-full rounded-lg px-3 py-2 text-sm border mb-2"
                style={{
                  backgroundColor: "var(--color-background)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text-primary)",
                }}
                placeholder="Mot de passe"
              />
              {passwordError && (
                <p className="text-xs mb-2" style={{ color: "var(--color-error)" }}>
                  {passwordError}
                </p>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => {
                    setPasswordModalOpen(false);
                    setSelectedResult(null);
                    setPasswordError(null);
                  }}
                  className="px-3 py-1.5 rounded-md text-xs font-medium"
                  style={{
                    backgroundColor: "var(--color-muted)",
                    color: "var(--color-text-primary)",
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={async () => {
                    if (!selectedResult) return;
                    await handleDownloadCsv(selectedResult.id, selectedResult.name, passwordValue);
                    if (!passwordError) {
                      setPasswordModalOpen(false);
                      setSelectedResult(null);
                    }
                  }}
                  className="px-3 py-1.5 rounded-md text-xs font-medium"
                  style={{
                    backgroundColor: "var(--color-primary)",
                    color: "var(--color-primary-foreground)",
                  }}
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <div className="h-14 md:hidden" />
    </div>
  );
};

export default ResultsAndDownloadCenter;